import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const OwnerCertificationModal = ({ isOpen, onClose, onSubmit, isSubmitting }) => {
  const [certifications, setCertifications] = useState({
    legalOwner: false,
    properlyTitled: false,
    maintainsInsurance: false,
    understandsNoInspection: false,
    agreesIndemnify: false
  });

  const [error, setError] = useState('');

  const handleCheckboxChange = (field) => {
    setCertifications(prev => ({ ...prev, [field]: !prev?.[field] }));
    setError('');
  };

  const handleSubmit = () => {
    const allChecked = Object.values(certifications)?.every(val => val === true);
    
    if (!allChecked) {
      setError('You must certify all statements before proceeding');
      return;
    }

    onSubmit(certifications);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-lg shadow-elevation-5 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-semibold text-foreground">Owner Listing Certification</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-smooth"
            disabled={isSubmitting}
          >
            <Icon name="X" size={20} strokeWidth={2} />
          </button>
        </div>

        <div className="mb-6">
          <div className="p-4 bg-primary/5 border border-primary rounded-md flex items-start space-x-3 mb-6">
            <Icon name="AlertCircle" size={20} color="var(--color-primary)" strokeWidth={2} />
            <div className="flex-1">
              <p className="font-body text-sm font-semibold text-foreground mb-1">Before Publishing a Listing</p>
              <p className="font-caption text-xs text-muted-foreground">
                Owners must certify the following statements. Providing false information may result in account termination.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Checkbox
              checked={certifications?.legalOwner}
              onChange={() => handleCheckboxChange('legalOwner')}
              label="I am the legal owner of this trailer or have full authority to rent it"
              required
            />

            <Checkbox
              checked={certifications?.properlyTitled}
              onChange={() => handleCheckboxChange('properlyTitled')}
              label="The trailer is properly titled, registered, and roadworthy"
              required
            />

            <Checkbox
              checked={certifications?.maintainsInsurance}
              onChange={() => handleCheckboxChange('maintainsInsurance')}
              label="I maintain any insurance required by law"
              required
            />

            <Checkbox
              checked={certifications?.understandsNoInspection}
              onChange={() => handleCheckboxChange('understandsNoInspection')}
              label="I understand Draggin Trailers LLC does not insure or inspect my trailer"
              required
            />

            <Checkbox
              checked={certifications?.agreesIndemnify}
              onChange={() => handleCheckboxChange('agreesIndemnify')}
              label="I agree to indemnify Draggin Trailers LLC from any claims arising from this listing"
              required
            />
          </div>

          {error && (
            <div className="mt-4 p-3 bg-error/10 border border-error rounded-md flex items-start space-x-2">
              <Icon name="AlertCircle" size={16} color="var(--color-error)" strokeWidth={2} />
              <p className="font-body text-sm text-error flex-1">{error}</p>
            </div>
          )}
        </div>

        <div className="p-4 bg-warning/10 border border-warning rounded-md flex items-start space-x-3 mb-6">
          <Icon name="AlertTriangle" size={20} color="var(--color-warning)" strokeWidth={2} />
          <div className="flex-1">
            <p className="font-body text-sm font-semibold text-foreground mb-1">Important Notice</p>
            <p className="font-caption text-xs text-muted-foreground">
              By certifying these statements, you acknowledge that you are solely responsible for the accuracy of this information and any consequences arising from false certification.
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
            iconName="Check"
            className="flex-1"
          >
            {isSubmitting ? 'Certifying...' : 'Certify & Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OwnerCertificationModal;